import type { CellKeyDownEvent, IMenuActionParams } from "ag-grid-community";

import { useMemoCallback } from "../../../utils/useMemoCallback";
import { useKeyboardShortcuts } from "./useKeyboardShortcuts";
import { useActivePageId } from "../../../data";
import { keysMatch, modifierKeyPressed, type TGridKeyboardShortcut } from "./utils";
import { copyRangeToClipboard, copyWithHeadersAndMonths } from "../copyRangeSelectionHelpers";

export const useOnCellKeyDown = () => {
  const pageId = useActivePageId();
  const shortcuts = useKeyboardShortcuts();

  const onCellKeyDown = (params: CellKeyDownEvent) => {
    if (!params || !pageId || !("column" in params)) return;

    // CellKeyDownEvent's type has "Event" as the type of the event property, but it is actually a KeyboardEvent. We need to cast it to the correct type.
    const event = params.event as KeyboardEvent;

    if (!("key" in event)) return;

    const editingCells = params.api.getEditingCells();
    if (editingCells && editingCells.length > 0) {
      return;
    }

    // check ctrl+shift+C before ctrl+C otherwise it wont work
    if (
      modifierKeyPressed(event, "ctrl") &&
      modifierKeyPressed(event, "shift") &&
      (event.key === "c" || event.key === "C")
    ) {
      if (params.api) {
        event.preventDefault();
        event.stopPropagation();
        const menuParams: IMenuActionParams = {
          api: params.api,
          column: null,
          node: null,
          value: undefined,
          context: undefined,
        };
        copyWithHeadersAndMonths(menuParams);
        return;
      }
    }

    // regular ctrl+C copy
    if (modifierKeyPressed(event, "ctrl") && (event.key === "c" || event.key === "C")) {
      if (params.api) {
        event.preventDefault();
        event.stopPropagation();
        copyRangeToClipboard(params.api);
        return;
      }
    }

    // Handle both Backspace (Mac Delete key) and Delete (Fn-Delete on Mac) to clear cells
    if (event.key === "Backspace" || event.key === "Delete") {
      event.preventDefault();
      event.stopPropagation();

      const cellRanges = params.api.getCellRanges();
      if (!cellRanges || cellRanges.length === 0) {
        return;
      }

      // Clear all cells
      for (const range of cellRanges) {
        const columns = range.columns || [];
        const startRow = range.startRow?.rowIndex ?? 0;
        const endRow = range.endRow?.rowIndex ?? 0;
        const minRow = Math.min(startRow, endRow);
        const maxRow = Math.max(startRow, endRow);

        for (let rowIndex = minRow; rowIndex <= maxRow; rowIndex++) {
          const rowNode = params.api.getDisplayedRowAtIndex(rowIndex);
          if (rowNode) {
            for (const column of columns) {
              // Check if cell is editable before clearing
              const colDef = column.getColDef();
              const isEditable =
                typeof colDef.editable === "function"
                  ? colDef.editable({
                      node: rowNode,
                      data: rowNode.data,
                      column,
                      api: params.api,
                      context: params.context,
                      colDef,
                    })
                  : colDef.editable;

              if (isEditable) {
                rowNode.setDataValue(column.getColId(), "");
              }
            }
          }
        }
      }
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (modifierKeyPressed(event, "ctrl") || modifierKeyPressed(event, "alt")) {
      const shortcutMatch = matchGridShortcut(event, shortcuts);
      if (shortcutMatch) {
        return shortcutMatch.action({ params, pageId });
      }
    }
  };

  return useMemoCallback(onCellKeyDown);
};

export function matchGridShortcut(
  event: KeyboardEvent,
  shortcuts: TGridKeyboardShortcut[],
): TGridKeyboardShortcut | null {
  for (const shortcut of shortcuts) {
    const shortcutMatch = shortcut.shortcuts.reduce((acc, sc) => {
      if (keysMatch(event, sc)) {
        acc.push(shortcut);
      }
      return acc;
    }, [] as TGridKeyboardShortcut[])?.[0];

    if (shortcutMatch) {
      return shortcutMatch;
    }
  }

  return null;
}
